import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/hooks';
import { Card } from '@/components/ui/Card';
import { TerminologyTab } from './experience/TerminologyTab';
import { CapabilityPacksTab } from './experience/CapabilityPacksTab';
import styles from './AdminExperiencePage.module.css';

/**
 * Experience configuration: what this institution's screens SAY (terminology) and
 * what they SHOW (capability packs).
 *
 * One page, two tabs, because the two settings are the same kind of decision made
 * by the same person — "our school calls a class a Form and does not run boarding"
 * — and separating them into two nav entries would make each look like a niche
 * feature instead of the two halves of the institution's voice.
 *
 * The tab bar copies AdminOrganisationPage's WAI-ARIA tablist wholesale: roving
 * tabindex, arrow keys move, Home/End jump. Same interaction, same class names,
 * so the two tabbed admin pages feel like one console rather than two authors.
 */

const NS = 'admin.experience';

const TABS = ['terminology', 'packs'] as const;

type TabId = (typeof TABS)[number];

export function AdminExperiencePage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const [tab, setTab] = useState<TabId>('terminology');
  const tabRefs = useRef(new Map<TabId, HTMLButtonElement>());

  const focusTab = (next: TabId) => {
    setTab(next);
    tabRefs.current.get(next)?.focus();
  };

  const moveFocus = (from: TabId, delta: number) => {
    const index = TABS.indexOf(from);
    focusTab(TABS[(index + delta + TABS.length) % TABS.length]);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.headerName}>{t(`${NS}.title`)}</h1>
          <p className={styles.headerSubtitle}>{t(`${NS}.subtitle`)}</p>
        </div>
      </header>

      <div role="tablist" aria-label={t(`${NS}.title`)} className={styles.tabList}>
        {TABS.map((id) => (
          <button
            key={id}
            ref={(node) => {
              if (node) tabRefs.current.set(id, node);
              else tabRefs.current.delete(id);
            }}
            type="button"
            role="tab"
            id={`experience-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`experience-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            className={tab === id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setTab(id)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                moveFocus(id, 1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                moveFocus(id, -1);
              } else if (event.key === 'Home') {
                event.preventDefault();
                focusTab(TABS[0]);
              } else if (event.key === 'End') {
                event.preventDefault();
                focusTab(TABS[TABS.length - 1]);
              }
            }}
          >
            {t(`${NS}.tabs.${id}`)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`experience-panel-${tab}`}
        aria-labelledby={`experience-tab-${tab}`}
        tabIndex={0}
        className={styles.tabPanel}
      >
        {!institutionId ? (
          <Card>
            <p className={styles.noContext}>{t(`${NS}.noContext`)}</p>
          </Card>
        ) : tab === 'terminology' ? (
          <TerminologyTab institutionId={institutionId} />
        ) : (
          <CapabilityPacksTab institutionId={institutionId} />
        )}
      </div>
    </div>
  );
}
